import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSignup } from '@/hooks/auth/use-signup';
import FadeIn from '@/components/animations/FadeIn';
import FormFields from './FormFields';
import SubmitButton from './SubmitButton';
import SignInLink from './SignInLink';

interface SignUpFormProps {
  isLoading?: boolean;
  onSwitchMode?: () => void; // Callback for switching to sign in mode
  onFormSubmit?: (email: string, password: string) => void; // Callback for form submission
}

const SIGNUP_FORM_ID = 'signup-form'; // Unique ID for the form

const SignUpForm: React.FC<SignUpFormProps> = ({ 
  isLoading = false, 
  onSwitchMode,
  onFormSubmit 
}) => {
  const { email, setEmail, password, setPassword, isSubmitting, handleSignup } = useSignup();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Use callback if provided, otherwise show captcha modal
    if (onFormSubmit) {
      onFormSubmit(email, password);
    } else {
      // Fallback for standalone usage - this shouldn't happen in normal flow
      console.warn('SignUpForm: No onFormSubmit callback provided');
    }
  };

  return (
    <motion.form 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      onSubmit={onSubmit} 
      className="space-y-5" 
      id={SIGNUP_FORM_ID}
    >
      <FormFields
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        isSubmitting={isSubmitting || isLoading}
      />
      
      <FadeIn delay={200}>
        <SubmitButton isSubmitting={isSubmitting || isLoading} />
      </FadeIn>
      
      <SignInLink onSwitchMode={onSwitchMode} />
    </motion.form>
  );
};

export default SignUpForm;
